import { useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import {
  GROUP_META,
  compareServerName,
  getGroup,
  getMetricSeverity,
  isOnlineStatus,
} from '../helpers';
import type { ServerGroup, ServerStatsRow, StatusFilter } from '../types';
import { ServerCard } from './ServerCard';

interface Props {
  servers: ServerStatsRow[];
  isLoading: boolean;
  activeFilter: StatusFilter;
  onFilterChange: (f: StatusFilter) => void;
}

interface AnnotatedServer extends ServerStatsRow {
  _severity: ReturnType<typeof getMetricSeverity>;
  _group: ServerGroup;
}

export function LatestStatusTab({
  servers,
  isLoading,
  activeFilter,
  onFilterChange,
}: Props) {
  const annotated: AnnotatedServer[] = useMemo(
    () =>
      servers.map((s) => ({
        ...s,
        _severity: getMetricSeverity(s),
        _group: getGroup(s.serverName),
      })),
    [servers],
  );

  const totals = useMemo(() => {
    const online = annotated.filter((s) => isOnlineStatus(s.status)).length;
    const warning = annotated.filter((s) => s._severity === 'warning').length;
    const critical = annotated.filter((s) => s._severity === 'critical').length;
    const offline = annotated.filter((s) => !isOnlineStatus(s.status)).length;
    return { total: annotated.length, online, warning, critical, offline };
  }, [annotated]);

  const filtered = useMemo(() => {
    if (activeFilter === 'all') return annotated;
    if (activeFilter === 'critical')
      return annotated.filter((s) => s._severity === 'critical');
    if (activeFilter === 'warning')
      return annotated.filter((s) => s._severity === 'warning');
    if (activeFilter === 'online')
      return annotated.filter((s) => isOnlineStatus(s.status));
    if (activeFilter === 'offline')
      return annotated.filter((s) => !isOnlineStatus(s.status));
    return annotated.filter((s) => s._group === activeFilter);
  }, [annotated, activeFilter]);

  const groups = useMemo(() => {
    const order: ServerGroup[] = ['db', 'ahm', 'blr'];
    return order
      .map((g) => ({
        key: g,
        label: GROUP_META[g].label,
        servers: filtered
          .filter((s) => s._group === g)
          .sort((a, b) => compareServerName(a.serverName, b.serverName)),
      }))
      .filter((g) => g.servers.length > 0);
  }, [filtered]);

  const FILTERS: { key: StatusFilter; label: string; tone?: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'critical', label: 'Critical', tone: 'critical' },
    { key: 'warning', label: 'Warning', tone: 'warning' },
    { key: 'online', label: 'Online', tone: 'online' },
    { key: 'offline', label: 'Offline', tone: 'offline' },
    { key: 'db', label: 'DB' },
    { key: 'ahm', label: 'AHM' },
    { key: 'blr', label: 'BLR' },
  ];

  return (
    <div className="space-y-4">
      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile label="Total" value={totals.total} tone="neutral" />
        <StatTile label="Online" value={totals.online} tone="positive" />
        <StatTile label="Warning" value={totals.warning} tone="warning" />
        <StatTile label="Critical" value={totals.critical} tone="critical" />
        <StatTile label="Offline" value={totals.offline} tone="muted" />
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => onFilterChange(f.key)}
            className={cn(
              'rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors',
              activeFilter === f.key
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Grouped cards */}
      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-card/50 py-10 text-center text-sm text-muted-foreground">
          No servers match this filter.
        </div>
      ) : (
        groups.map((group) => (
          <div key={group.key} className="space-y-2">
            <div className="flex items-baseline justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground">
                {group.label}
              </h3>
              <span className="text-[11px] text-muted-foreground">
                {group.servers.length} server
                {group.servers.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {group.servers.map((server) => (
                <ServerCard key={server.serverName} server={server} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'positive' | 'warning' | 'critical' | 'muted';
}) {
  const toneClass: Record<typeof tone, string> = {
    neutral: 'text-primary',
    positive: 'text-emerald-700',
    warning: 'text-amber-700',
    critical: 'text-rose-700',
    muted: 'text-muted-foreground',
  };
  return (
    <div className="flex flex-col rounded-md border border-border bg-card px-3 py-2 shadow-sm">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        className={cn(
          'text-2xl font-semibold tabular-nums',
          toneClass[tone],
        )}
      >
        {value.toLocaleString()}
      </span>
    </div>
  );
}
